// WellManager.js
import React, { useState, useEffect, useCallback } from 'react';
import '../App.css';
import './WellManager.css';
import Toast from '../components/Toast';
import { DataTable } from '../components/tables';
import TreeDataExplorer from '../components/TreeDataExplorer';
import  DataMap  from '../components/DataMap';
import { CalendarView } from '../components/calendar';
import { DataForm, IndustryStandardsModal } from '../components/forms';
import { useLocation } from 'react-router-dom';
import { apiFetch } from '../api/api';
import { normalizeDate } from '../utils/utils';

export default function WellManager() {
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('token'));
  const [role, setRole] = useState(localStorage.getItem('role') || '');
  const [formData, setFormData] = useState({});
  const [wells, setWells] = useState([]);
  const [wellAttachments, setWellAttachments] = useState([]);
  const [wellPlanData, setWellPlanData] = useState({});
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVisible, setToastVisible] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('well');
  
  // New attachment form states
  const [showAttachmentForm, setShowAttachmentForm] = useState(false);
  const [selectedWellForAttachment, setSelectedWellForAttachment] = useState(null);
  const [dailyReports, setDailyReports] = useState([]);
  
  const [tableColumns, setTableColumns] = useState([]);
  const [columnNames, setColumnNames] = useState([]);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  
  // View toggle state
  const [viewMode, setViewMode] = useState('table'); // 'table', 'tree', 'map', or 'calendar'
  
  // Filter states
  const [columnFilters, setColumnFilters] = useState({});
  const [filteredWells, setFilteredWells] = useState([]);
  
  // Industry Standards modal state
  const [showIndustryStandards, setShowIndustryStandards] = useState(false);

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const editParam = params.get('edit');

  useEffect(() => {
    const fetchColumnConfig = async () => {
      setIsLoadingConfig(true);
      try {
        const allConfigs = await apiFetch('/api/config/columns');
        const wellConfig = Array.isArray(allConfigs) 
            ? allConfigs.find(config => config.tableName === "Well") 
            : null;

        if (wellConfig && Array.isArray(wellConfig.columns)) {
          const sortedColumns = wellConfig.columns.sort((a, b) => a.order - b.order);
          setTableColumns(sortedColumns);
          const names = sortedColumns.map(col => col.name);
          setColumnNames(names);
          const emptyData = names.reduce((obj, col) => ({ ...obj, [col]: '' }), {});
          setFormData({ ...emptyData, attachmentSubject: '', attachmentDescription: '' });
        } else {
          console.error("CRITICAL: 'Well' table configuration not found or is malformed via API.");
          setTableColumns([]);
          setColumnNames([]);
          setToastMessage('Failed to load table column configuration for Wells.');
          setToastVisible(true);
        }
      } catch (err) {
        console.error('Fetch column config error:', err);
        setToastMessage('Failed to fetch table column configuration: ' + (err.message || 'Unknown error'));
        setToastVisible(true);
        setTableColumns([]);
        setColumnNames([]);
      }
      setIsLoadingConfig(false);
    };
    if (isAuthenticated) {
        fetchColumnConfig();
    }
  }, [isAuthenticated]);

  // Filter wells based on column filters
  const applyFilters = useCallback((wellsData, filters) => {
    if (Object.keys(filters).length === 0) return wellsData;
    
    return wellsData.filter(well => {
      return Object.entries(filters).every(([columnName, filterConfig]) => {
        const cellValue = well[columnName];
        const { type, value, operator } = filterConfig;
        
        if (!value) return true; // No filter value
        
        // Handle null/undefined values
        if (cellValue === null || cellValue === undefined) {
          return value.toLowerCase() === 'null' || value.toLowerCase() === 'empty';
        }
        
        const stringValue = String(cellValue).toLowerCase();
        const filterValue = String(value).toLowerCase();
        
        switch (type) {
          case 'exact':
            return stringValue === filterValue;
            
          case 'wildcard':
            const regexPattern = filterValue.replace(/\*/g, '.*').replace(/%/g, '.*');
            const regex = new RegExp(`^${regexPattern}$`, 'i');
            return regex.test(stringValue);
            
          case 'numeric':
            const numValue = parseFloat(cellValue);
            const numFilter = parseFloat(value);
            if (isNaN(numValue) || isNaN(numFilter)) return false;
            
            switch (operator) {
              case '<': return numValue < numFilter;
              case '>': return numValue > numFilter;
              case '=': return numValue === numFilter;
              case '<=': return numValue <= numFilter;
              case '>=': return numValue >= numFilter;
              default: return numValue === numFilter;
            }
            
          case 'date':
            const dateValue = new Date(cellValue);
            if (isNaN(dateValue.getTime())) return false;
            
            if (operator === 'range') {
              const [startDate, endDate] = value.split(' to ').map(d => new Date(d));
              return dateValue >= startDate && dateValue <= endDate;
            } else if (operator === 'year') {
              return dateValue.getFullYear().toString() === filterValue;
            } else if (operator === 'month') {
              const yearMonth = `${dateValue.getFullYear()}-${String(dateValue.getMonth() + 1).padStart(2, '0')}`;
              return yearMonth === filterValue;
            } else {
              const filterDate = new Date(value);
              return dateValue.toDateString() === filterDate.toDateString();
            }
            
          default:
            return stringValue.includes(filterValue);
        }
      });
    });
  }, []);

  // Fetch wells data with proper error handling
  const fetchWells = useCallback(async () => {
    try {
      const data = await apiFetch('/api/wells');
      const wellsData = Array.isArray(data) ? data : [];
      setWells(wellsData);
      
      // Apply current filters to new data
      const filtered = applyFilters(wellsData, columnFilters);
      setFilteredWells(filtered);
      
      return wellsData;
    } catch (err) {
      console.error('❌ Fetch error:', err);
      setToastMessage('Failed to fetch wells data: ' + (err.message || 'Unknown error'));
      setToastVisible(true);
      return [];
    }
  }, [columnFilters, applyFilters]);

  const fetchAttachmentsForWell = async (wellId) => {
    try {
      const all = await apiFetch('/api/attachments');
      const filtered = all.filter(att => att.WellID === wellId);
      setWellAttachments(filtered);
    } catch (err) {
      console.error('Failed to load attachments for well:', err);
    }
  };

  const handlePlanChange = (field, value) => {
    setWellPlanData(prev => ({ ...prev, [field]: value }));
  };

  const handleEdit = useCallback(async (well) => {
    const formattedWell = { ...well };
    if (formattedWell.SpudDate) {
      formattedWell.SpudDate = normalizeDate(formattedWell.SpudDate);
    }
    setFormData(formattedWell);

    const planData = await apiFetch (`/api/db/WellPlan`);
    const planArray = Array.isArray(planData) ? planData : [];
    const match = planArray.find(p => p.WellID === well.WellID);
    setWellPlanData(match || { WellID: well.WellID });

    setEditId(well.WellID);
    fetchAttachmentsForWell(well.WellID);
    setShowForm(true);
  }, []);

  useEffect(() => {
    if (isAuthenticated && !isLoadingConfig) {
      fetchWells();
    }
  }, [isAuthenticated, fetchWells, isLoadingConfig]);

  // Update filtered wells when filters change
  useEffect(() => {
    const filtered = applyFilters(wells, columnFilters);
    setFilteredWells(filtered);
  }, [wells, columnFilters, applyFilters]);

  // Load daily reports when authenticated
  useEffect(() => {
    const loadDailyReports = async () => {
      try {
        const data = await apiFetch('/api/ddr');
        const reportsData = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
        setDailyReports(reportsData);
      } catch (err) {
        console.error('Failed to fetch daily reports:', err);
        setDailyReports([]);
      }
    };

    if (isAuthenticated) {
      loadDailyReports();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (editParam && wells.length > 0 && !isLoadingConfig) {
      const target = wells.find(w => w.WellID === parseInt(editParam));
      if (target) handleEdit(target);
    }
  }, [wells, editParam, handleEdit, isLoadingConfig]);

  // handleChange no longer needed - DataForm manages its own state

  const handleAdd = () => {
    if (columnNames.length > 0) {
        const emptyData = columnNames.reduce((obj, col) => ({ ...obj, [col]: '' }), {});
        setFormData({ ...emptyData, WellID: null });
    }
    setEditId(null);
    setWellPlanData({});
    setWellAttachments([]);
    setActiveTab('well');
    setShowForm(true);
  };

  const handleCancel = () => {
    if (columnNames.length > 0) {
        const emptyData = columnNames.reduce((obj, col) => ({ ...obj, [col]: '' }), {});
        setFormData({ ...emptyData, WellID: null });
    }
    setEditId(null);
    setShowForm(false);
    setWellPlanData({});
    setWellAttachments([]);
  };

  // handleSave logic moved to DataForm onSave callback

  // Attachment form handlers
  const handleInsertAttachment = (wellId) => {
    setSelectedWellForAttachment(wellId);
    setShowAttachmentForm(true);
  };

  const handleAttachmentSuccess = (message) => {
    setToastMessage(message);
    setToastVisible(true);
    setShowAttachmentForm(false);
    // Refresh wells and attachments
    fetchWells();
    if (selectedWellForAttachment) {
      fetchAttachmentsForWell(selectedWellForAttachment);
    }
  };

  // Filter management functions
  const handleColumnFilter = (columnName, filterConfig) => {
    setColumnFilters(prev => ({
      ...prev,
      [columnName]: filterConfig
    }));
  };

  const clearColumnFilter = (columnName) => {
    setColumnFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[columnName];
      return newFilters;
    });
  };

  const clearAllFilters = () => {
    setColumnFilters({});
  };

  const handleDelete = async (row) => {
    // Extract the WellID from the row object passed by DataTable or context menu
    let wellId = null;
    if (row && typeof row === 'object') {
      wellId = row.WellID || row.id || row.Id || row._id;
    } else {
      wellId = row;
    }

    if (!wellId || isNaN(Number(wellId))) {
      setToastMessage('Delete failed: Invalid WellID.');
      setToastVisible(true);
      console.error('Delete failed: Invalid row or WellID:', row);
      return;
    }

    if (!window.confirm('Are you sure you want to delete this well?')) return;
    try {
      console.log('Deleting WellID:', wellId, 'from row:', row);
      await apiFetch(`/api/wells/${wellId}`, { method: 'DELETE' });
      const updatedWells = await apiFetch('/api/wells');
      setWells(Array.isArray(updatedWells) ? updatedWells : []);
      setToastMessage('Well deleted successfully!');
      setToastVisible(true);
      setTimeout(() => setToastVisible(false), 3000);
    } catch (err) {
      console.error('Delete failed:', err, 'Row:', row);
      setToastMessage('Delete failed: ' + (err.message || 'Unknown error'));
      setToastVisible(true);
    }
  };

  if (isLoadingConfig && isAuthenticated) {
    return <div className="app-container"><h2>Loading Well Configuration...</h2></div>;
  }

  return (
    <div className="app-container">
      <div className="page-header">
        <div className="header-left">
          <h2>Well Manager</h2>
          {/* Add Button moved to header - Show only in table view and when not editing */}
          {(role === 'admin' || role === 'read-write') && 
           tableColumns.length > 0 && 
           viewMode === 'table' && 
           !showForm && (
            <button className="add-button-header" onClick={handleAdd}>Add Well</button>
          )}
        </div>
        <div className="header-controls">
          {Object.keys(columnFilters).length > 0 && (
            <button className="clear-filters-button" onClick={clearAllFilters}>
              Clear Filters ({Object.keys(columnFilters).length})
            </button>
          )}
          
 
          
          <div className="view-toggle-compact">
            <button 
              className={`view-btn-compact ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Table View"
            >
              📊
            </button>
            <button 
              className={`view-btn-compact ${viewMode === 'tree' ? 'active' : ''}`}
              onClick={() => setViewMode('tree')}
              title="Tree View"
            >
              🌳
            </button>
            <button 
              className={`view-btn-compact ${viewMode === 'map' ? 'active' : ''}`}
              onClick={() => setViewMode('map')}
              title="Map View"
            >
              🗺️
            </button>
            <button 
              className={`view-btn-compact ${viewMode === 'calendar' ? 'active' : ''}`}
              onClick={() => setViewMode('calendar')}
              title="Calendar View"
            >
              📅
            </button>
          </div>
        </div>
      </div>
      <div className="split-view">
        <div className="list-section">

          {/* Data Display */}
          {tableColumns.length > 0 ? (
            viewMode === 'table' ? (
              <DataTable
                data={Object.keys(columnFilters).length > 0 ? filteredWells : wells}
                columns={tableColumns}
                role={role}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onDataChange={fetchWells}
                title="Well List"
                tableName="Well"
                itemsPerPage={50}
                // Filter props
                onColumnFilter={handleColumnFilter}
                onClearColumnFilter={clearColumnFilter}
                columnFilters={columnFilters}
              />
            ) : viewMode === 'tree' ? (
              <TreeDataExplorer
                data={Object.keys(columnFilters).length > 0 ? filteredWells : wells}
                columns={tableColumns}
                title="Well Data Tree"
                onDataChange={fetchWells}
              />
            ) : viewMode === 'map' ? (
              <DataMap
                data={Object.keys(columnFilters).length > 0 ? filteredWells : wells}
                columns={tableColumns}
                title="Well Locations Map"
                height="700px"
              />
            ) : viewMode === 'calendar' ? (
              <CalendarView
                data={Object.keys(columnFilters).length > 0 ? filteredWells : wells}
                title="Wells Calendar"
                dateField="SpudDate"
                idField="WellID"
                linkTemplate="/welldetail/{id}"
                cardFields={[
                  { key: 'WellName', label: 'Well Name', primary: true },
                  { key: 'Operator', label: 'Operator' },
                  { key: 'Field', label: 'Field' },
                  { key: 'WellType', label: 'Type' },
                  { key: 'WellStatus', label: 'Status' },
                  { key: 'TotalDepth', label: 'Total Depth (m)' }
                ]}
                colorField="WellType"
                colorMap={{
                  'Exploration': '#16a085',
                  'Development': '#2c3e50',
                  'Production': '#27ae60'
                }}
                defaultColor="#34495e"
                onCardClick={(item) => {
                  console.log('Calendar card clicked:', item);
                  handleEdit(item);
                }}
              />
            ) : null
          ) : !isLoadingConfig && <p>Well table configuration could not be loaded. Cannot display well data.</p>}
        </div>

      {/* DataForm Modal - Centered */}
      {showForm && (
        <DataForm
          data={formData}
          tableName="Well"
          title={editId ? 'Edit Well' : 'Add New Well'}
          skipAutoSave={true} // Custom prop to skip DataForm's automatic save
          onSave={async (data) => {
            setIsSaving(true);
            try {
              // Save Well data using the wells API endpoint
              const method = editId ? 'PUT' : 'POST';
              const url = editId ? `/api/wells/${editId}` : `/api/wells`;
              const wellPayload = { ...data };
              
              // Remove any attachment-related fields from well payload
              delete wellPayload.attachmentFile;
              delete wellPayload.attachmentSubject;
              delete wellPayload.attachmentDescription;
              
              // For new wells, ensure WellID is not included in the payload
              if (!editId) {
                  delete wellPayload.WellID;
              } else {
                  wellPayload.WellID = editId;
              }

              const wellRes = await apiFetch(url, {
                method,
                body: JSON.stringify(wellPayload)
              });

              const savedWellId = editId || (wellRes.wellId || wellRes.WellID);
              if (!savedWellId && !editId) {
                console.warn("No WellID returned from server, but save may have succeeded");
              }

              // Refresh the wells list
              await fetchWells();
              
              setToastMessage('Well saved successfully!');
              setToastVisible(true);
              handleCancel();
            } catch (err) {
              console.error('Save failed:', err);
              setToastMessage('Save failed: ' + (err.message || 'Unknown error'));
              setToastVisible(true);
              throw err; // Re-throw so DataForm can handle error display
            } finally {
              setIsSaving(false);
              setTimeout(() => setToastVisible(false), 3000);
            }
          }}
          onCancel={handleCancel}
        />
      )}
      </div>
      {/* Industry Standards Modal */}
      {showIndustryStandards && (
        <IndustryStandardsModal
          isOpen={showIndustryStandards}
          onClose={() => setShowIndustryStandards(false)}
          wellId={editId}
          wellName={editId ? formData?.WellName : null}
        />
      )}

      {toastVisible && (
        <Toast message={toastMessage} onClose={() => setToastVisible(false)} />
      )}

    </div>
  );
}
